import { useState, useEffect, useCallback } from "react";
import {
  Mail, Send, Save, Trash2, FileText, Users, Radio,
  Headphones, Loader2, CheckCircle2, AlertCircle,
  Zap, Clock, BarChart3, Shield, ChevronRight
} from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const AUDIENCE_OPTIONS_ADMIN = [
  { id: "all_users", label: "All Users", icon: Users },
  { id: "station_owners", label: "Station Owners", icon: Radio },
  { id: "listeners", label: "Listeners", icon: Headphones },
];

const AUDIENCE_OPTIONS_OWNER = [
  { id: "followers", label: "My Followers", icon: Users },
];

const CATEGORY_COLORS = {
  engagement: "text-green-400 bg-green-500/10 border-green-500/20",
  content: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  update: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  event: "text-amber-400 bg-amber-500/10 border-amber-500/20",
};

export const EmailTab = ({ currentUser, authToken, isAdmin }) => {
  const [builtinTemplates, setBuiltinTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [usage, setUsage] = useState({ plan: "", limit: 0, used: 0, remaining: 0 });

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState(isAdmin ? "all_users" : "followers");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");

  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [view, setView] = useState("compose"); // compose | templates | history

  const headers = { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" };
  const audienceOptions = isAdmin ? AUDIENCE_OPTIONS_ADMIN : AUDIENCE_OPTIONS_OWNER;

  const fetchAll = useCallback(async () => {
    const [builtinRes, customRes, campaignRes, usageRes] = await Promise.all([
      fetch(`${BACKEND_URL}/api/email/templates/builtin`, { headers }),
      fetch(`${BACKEND_URL}/api/email/templates`, { headers }),
      fetch(`${BACKEND_URL}/api/email/campaigns`, { headers }),
      fetch(`${BACKEND_URL}/api/email/usage`, { headers }),
    ]);
    if (builtinRes.ok) setBuiltinTemplates(await builtinRes.json());
    if (customRes.ok) setCustomTemplates(await customRes.json());
    if (campaignRes.ok) setCampaigns(await campaignRes.json());
    if (usageRes.ok) setUsage(await usageRes.json());
  }, [authToken]); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBody(tpl.html_body);
    setSelectedTemplateId(tpl.id || tpl.name);
    setView("compose");
    setMsg({ type: "success", text: `Loaded "${tpl.name}"` });
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setMsg({ type: "error", text: "Subject and body are required." });
      return;
    }
    if (usage.remaining <= 0 && !isAdmin) {
      setMsg({ type: "error", text: "Monthly email limit reached. Upgrade your plan for more sends." });
      return;
    }
    setSending(true);
    setMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/send`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          template_id: selectedTemplateId || "custom",
          subject,
          html_body: body,
          audience,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: data.message });
        setSubject("");
        setBody("");
        setSelectedTemplateId("");
        fetchAll();
      } else {
        setMsg({ type: "error", text: data.detail || "Send failed" });
      }
    } catch {
      setMsg({ type: "error", text: "Network error" });
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !subject.trim() || !body.trim()) {
      setMsg({ type: "error", text: "Template name, subject, and body required." });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/templates`, {
        method: "POST",
        headers,
        body: JSON.stringify({ name: templateName, subject, html_body: body }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: "Template saved!" });
        setTemplateName("");
        fetchAll();
      } else {
        setMsg({ type: "error", text: data.detail || "Save failed" });
      }
    } catch {
      setMsg({ type: "error", text: "Network error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    await fetch(`${BACKEND_URL}/api/email/templates/${id}`, { method: "DELETE", headers });
    fetchAll();
  };

  const usagePercent = usage.limit > 0 ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 0;
  const usageColor = usagePercent >= 90 ? "bg-red-500" : usagePercent >= 70 ? "bg-amber-500" : "bg-purple-500";

  return (
    <div className="space-y-6" data-testid="email-tab">
      {/* Usage Bar */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5" data-testid="email-usage-bar">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-purple-400" />
            <span className="text-white/70 text-sm font-medium">Monthly Email Quota</span>
          </div>
          <span className="text-white text-sm font-bold" data-testid="email-usage-count">
            {usage.used.toLocaleString()} / {usage.limit.toLocaleString()}
          </span>
        </div>
        <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${usageColor}`}
            style={{ width: `${usagePercent}%` }}
            data-testid="email-usage-progress"
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-white/30 text-xs capitalize">{usage.plan} Plan</span>
          <span className="text-white/30 text-xs">{usage.remaining.toLocaleString()} remaining</span>
        </div>
      </div>

      {/* View Switcher */}
      <div className="flex gap-1 bg-white/[0.03] border border-white/10 rounded-xl p-1 w-fit">
        {[
          { id: "compose", label: "Compose", icon: Mail },
          { id: "templates", label: "Templates", icon: FileText },
          { id: "history", label: "Sent", icon: Clock },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              view === t.id
                ? "bg-purple-500/20 text-purple-400"
                : "text-white/40 hover:text-white/70"
            }`}
            data-testid={`email-view-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Status Message */}
      {msg && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${
            msg.type === "success"
              ? "bg-green-500/10 border border-green-500/20 text-green-400"
              : "bg-red-500/10 border border-red-500/20 text-red-400"
          }`}
          data-testid="email-msg"
        >
          {msg.type === "success" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {/* ── COMPOSE VIEW ── */}
      {view === "compose" && (
        <div className="space-y-5">
          {/* Quick Template Picker */}
          <div>
            <label className="text-white/50 text-xs mb-2 block flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" /> Quick Start — Pick a Template
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {builtinTemplates.map(tpl => (
                <button
                  key={tpl.id}
                  onClick={() => loadTemplate(tpl)}
                  className={`text-left p-4 rounded-xl border transition hover:scale-[1.02] ${
                    selectedTemplateId === tpl.id
                      ? "bg-purple-500/10 border-purple-500/30"
                      : "bg-white/[0.02] border-white/8 hover:border-white/15"
                  }`}
                  data-testid={`builtin-tpl-${tpl.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-sm font-medium">{tpl.name}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-white/20" />
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${CATEGORY_COLORS[tpl.category] || "text-white/40 bg-white/5 border-white/10"}`}>
                    {tpl.category}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Audience Selector */}
          <div>
            <label className="text-white/50 text-xs mb-2 block">Send To</label>
            <div className="flex gap-2 flex-wrap">
              {audienceOptions.map(a => (
                <button
                  key={a.id}
                  onClick={() => setAudience(a.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition border ${
                    audience === a.id
                      ? "bg-purple-500/15 border-purple-500/30 text-purple-400"
                      : "bg-white/[0.03] border-white/10 text-white/40 hover:text-white/70"
                  }`}
                  data-testid={`audience-${a.id}`}
                >
                  <a.icon className="w-4 h-4" /> {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="text-white/50 text-xs mb-1.5 block">Subject Line</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500/50 transition"
              placeholder="Your email subject..."
              data-testid="email-subject"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-white/50 text-xs mb-1.5 block">Email Body (HTML supported)</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={8}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500/50 transition font-mono resize-y"
              placeholder="Write your email content here..."
              data-testid="email-body"
            />
          </div>

          {/* Unsubscribe Notice */}
          <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/5 text-white/30 text-xs">
            <Shield className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              An unsubscribe link is automatically added to every email. Recipients who unsubscribe will be excluded from future sends.
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleSend}
              disabled={sending || (!isAdmin && usage.remaining <= 0)}
              className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-xl transition"
              data-testid="email-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending..." : "Send Campaign"}
            </button>

            <div className="flex items-center gap-2">
              <input
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50 transition w-44"
                placeholder="Save as template..."
                data-testid="template-name-input"
              />
              <button
                onClick={handleSaveTemplate}
                disabled={saving}
                className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-4 py-2.5 rounded-xl transition text-sm disabled:opacity-40"
                data-testid="save-template-btn"
              >
                <Save className="w-4 h-4" /> {saving ? "..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TEMPLATES VIEW ── */}
      {view === "templates" && (
        <div className="space-y-4">
          {/* Built-in */}
          <div>
            <h3 className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">Built-in Templates</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {builtinTemplates.map(tpl => (
                <div
                  key={tpl.id}
                  className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex items-center justify-between"
                  data-testid={`tpl-builtin-${tpl.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium text-sm">{tpl.name}</h4>
                    <p className="text-white/40 text-xs truncate mt-0.5">{tpl.subject}</p>
                  </div>
                  <button
                    onClick={() => loadTemplate(tpl)}
                    className="text-purple-400 hover:text-purple-300 text-xs font-medium ml-3 shrink-0 transition"
                  >
                    Use
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Custom */}
          <div>
            <h3 className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">
              Your Saved Templates
            </h3>
            {customTemplates.length === 0 ? (
              <p className="text-white/20 text-sm">No custom templates yet. Compose an email and save it as a template.</p>
            ) : (
              <div className="space-y-2">
                {customTemplates.map(tpl => (
                  <div
                    key={tpl.id}
                    className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex items-center justify-between"
                    data-testid={`tpl-custom-${tpl.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium text-sm truncate">{tpl.name}</h4>
                      <p className="text-white/40 text-xs truncate">{tpl.subject}</p>
                    </div>
                    <div className="flex items-center gap-3 ml-4 shrink-0">
                      <button
                        onClick={() => loadTemplate(tpl)}
                        className="text-purple-400 hover:text-purple-300 text-xs font-medium transition"
                      >
                        Use
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(tpl.id)}
                        className="text-red-400/40 hover:text-red-400 transition"
                        data-testid={`delete-tpl-${tpl.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── HISTORY VIEW ── */}
      {view === "history" && (
        <div className="space-y-3">
          {campaigns.length === 0 ? (
            <p className="text-white/20 text-sm">No campaigns sent yet.</p>
          ) : (
            campaigns.map((c, i) => (
              <div
                key={c.campaign_id || i}
                className="bg-white/[0.03] border border-white/10 rounded-xl p-4"
                data-testid={`campaign-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium text-sm truncate flex-1 mr-3">{c.subject}</h4>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                      c.status === "completed"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-amber-500/20 text-amber-400"
                    }`}
                  >
                    {c.status?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-white/40 text-xs flex-wrap">
                  <span>To: <span className="capitalize">{c.audience?.replace("_", " ")}</span></span>
                  <span>Sent: {c.sent || 0}/{c.total || 0}</span>
                  {(c.suppressed || 0) > 0 && <span className="text-amber-400">Suppressed: {c.suppressed}</span>}
                  {(c.failed || 0) > 0 && <span className="text-red-400">Failed: {c.failed}</span>}
                  <span>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
